// Typed client for the credit endpoints. Every call is session-scoped, so
// credentials must ride along — the API derives the account from the cookie
// rather than from anything in the URL.

import { apiFetch } from "../lib/api";

export type PlanId = "free" | "pro" | "business";
export type UsageKind = "build" | "cloud" | "ai";
export type GrantType =
  | "daily_build"
  | "cloud_grant"
  | "ai_grant"
  | "monthly"
  | "rollover"
  | "topup"
  | "bonus";

export interface GrantBreakdownRow {
  grantId: string;
  type: GrantType;
  scope: UsageKind | "general";
  remainingCredits: number;
  amountCredits: number;
  expiresAt: string;
}

export interface AutoTopupSettings {
  enabled: boolean;
  thresholdCredits: number;
  amountCredits: number;
}

export interface CreditBalance {
  accountId: string;
  planId: PlanId;
  planLabel: string;
  totalCredits: number;
  generalCredits: number;
  usageSpecificCredits: Record<UsageKind, number>;
  grants: GrantBreakdownRow[];
  monthlyCredits: number;
  billingCycle: "monthly" | "annual";
  currentPeriodEnd?: string;
  autoTopup?: AutoTopupSettings;
  checkInLevelCredits: number;
  cloudJobBudgetCredits: number;
  dailyBuildRemaining: number;
}

export interface HistoryEntry {
  id: string;
  event: string;
  credits: number;
  balanceAfter: number;
  occurredAt: string;
  grantType?: GrantType;
  usageKind?: UsageKind;
  projectId?: string;
  description?: string;
}

export interface UsageRecord {
  id: string;
  usageKind: UsageKind;
  credits: number;
  occurredAt: string;
  projectId?: string;
  model?: string;
  inputTokens?: number;
  outputTokens?: number;
}

export interface UsageSummary {
  totalCredits: number;
  byKind: Record<UsageKind, number>;
  byProject: Array<{ projectId: string; credits: number }>;
  byModel: Array<{ model: string; credits: number }>;
  records: UsageRecord[];
}

export interface CreditTier {
  monthlyCredits: number;
  monthlyPriceCents: number;
  annualPriceCents: number;
}

export interface PlanCatalogEntry {
  id: PlanId;
  label: string;
  dailyBuildCredits: number;
  monthlyDailyCap: number | null;
  cloudGrantCredits: number;
  aiGrantCredits: number;
  rolloverEnabled: boolean;
  topupsEnabled: boolean;
  autoTopupEnabled: boolean;
  tiers: CreditTier[];
}

export class BillingApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "BillingApiError";
  }
}

async function json<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, { credentials: "include", ...init });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new BillingApiError(body?.error ?? `Request failed (${res.status})`, res.status);
  }
  return (await res.json()) as T;
}

function post<T>(path: string, body?: unknown, idempotent = false): Promise<T> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (idempotent) headers["Idempotency-Key"] = `web:${crypto.randomUUID()}`;
  return json<T>(path, {
    method: "POST",
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export function fetchBalance(): Promise<CreditBalance> {
  return json<CreditBalance>("/credits/balance");
}

export function fetchHistory(limit = 100): Promise<{ entries: HistoryEntry[] }> {
  return json(`/credits/history?limit=${limit}`);
}

export function fetchUsage(since?: string): Promise<UsageSummary> {
  const qs = since ? `?since=${encodeURIComponent(since)}` : "";
  return json<UsageSummary>(`/credits/usage${qs}`);
}

export function fetchPlans(): Promise<{ plans: PlanCatalogEntry[] }> {
  return json("/credits/plans");
}

export function startTierCheckout(args: {
  planId: Exclude<PlanId, "free">;
  monthlyCredits: number;
  billingCycle: "monthly" | "annual";
}): Promise<{ url: string | null }> {
  return post("/credits/checkout/subscription", args, true);
}

export function startTopupCheckout(credits: number): Promise<{ url: string | null }> {
  return post("/credits/checkout/topup", { credits }, true);
}

export function openBillingPortal(): Promise<{ url: string }> {
  return post("/credits/portal", undefined, true);
}

export function saveAutoTopup(
  settings: AutoTopupSettings,
): Promise<{ autoTopup: AutoTopupSettings }> {
  return post("/credits/auto-topup", settings);
}

export function saveCheckInLevel(
  credits: number,
): Promise<{ checkInLevelCredits: number }> {
  return post("/credits/check-in-level", { credits });
}

export function saveCloudJobBudget(
  credits: number,
): Promise<{ cloudJobBudgetCredits: number }> {
  return json("/credits/settings/cloud-job-budget", {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ credits }),
  });
}

// ── Formatting ──────────────────────────────────────────────────────────────

export function formatCredits(credits: number): string {
  // Whole numbers read as counts; fractions need two places or a 0.4-credit
  // edit renders as "0".
  return Number.isInteger(credits)
    ? credits.toLocaleString()
    : credits.toFixed(2);
}

export function formatUsd(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

const GRANT_LABELS: Record<GrantType, string> = {
  monthly: "Monthly credits",
  rollover: "Rollover credits",
  topup: "Top-up credits",
  bonus: "Bonus credits",
  daily_build: "Daily build credits",
  cloud_grant: "Hosting credits",
  ai_grant: "AI credits",
};

export function grantLabel(type: GrantType): string {
  return GRANT_LABELS[type] ?? type;
}

const EVENT_LABELS: Record<string, string> = {
  spend: "Credits used",
  monthly_granted: "Monthly credits added",
  monthly_expired: "Monthly credits expired",
  rollover_granted: "Rollover credits added",
  rollover_expired: "Rollover credits expired",
  daily_granted: "Daily credits added",
  daily_expired: "Daily credits expired",
  cloud_grant_granted: "Hosting credits added",
  cloud_grant_expired: "Hosting credits expired",
  ai_grant_granted: "AI credits added",
  ai_grant_expired: "AI credits expired",
  topup_granted: "Top-up added",
  topup_expired: "Top-up expired",
  bonus_granted: "Bonus credits added",
  bonus_expired: "Bonus credits expired",
};

export function eventLabel(event: string): string {
  return EVENT_LABELS[event] ?? event;
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
